import { useState, useEffect, useCallback } from 'react';
import { getPhotoSasUrl, invalidatePhotoUrl, peekCachedPhotoUrl } from '../services/api/imageService';

const MAX_RETRIES = 5;

/**
 * Custom hook to fetch and manage image URL from photoId
 * @param photoId - The photo ID to fetch URL for
 * @returns Object containing imageUrl, loading state, error state, and refetch callback
 */
export const useImageUrl = (photoId: string | number | null | undefined) => {
  const [imageUrl, setImageUrl] = useState<string>(() => peekCachedPhotoUrl(photoId));
  const [loading, setLoading] = useState<boolean>(() => !!photoId && !peekCachedPhotoUrl(photoId));
  const [error, setError] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);

  useEffect(() => {
    let isMounted = true;

    const fetchImageUrl = async () => {
      if (!photoId) {
        setImageUrl('');
        setError(false);
        setLoading(false);
        return;
      }

      const cachedUrl = peekCachedPhotoUrl(photoId);
      if (cachedUrl) {
        setImageUrl(cachedUrl);
        setError(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(false);

      try {
        const url = await getPhotoSasUrl(photoId);

        if (isMounted) {
          if (url) {
            setImageUrl(url);
            setError(false);
          } else {
            setImageUrl('');
            setError(true);
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching image URL:', err);
          setImageUrl('');
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchImageUrl();

    return () => {
      isMounted = false;
    };
  }, [photoId, retryCount]);

  const refetch = useCallback(() => {
    if (photoId && retryCount < MAX_RETRIES) {
      invalidatePhotoUrl(photoId);
      setRetryCount(c => c + 1);
    }
  }, [photoId, retryCount]);

  useEffect(() => {
    setRetryCount(0);
  }, [photoId]);

  return { imageUrl, loading, error, refetch };
};
